import type { Media, MediaKind } from "./api/types";
import type { ApiClient } from "./api/client";
import { unwrap } from "./api/errors";

export interface UploadMeta {
  property: string;
  room?: string | null;
  wall?: string | null;
  panel?: string | null;
  kind: MediaKind;
  note?: string;
  /** Rasm olingan vaqt (ISO): offlayn yuklanganda ham haqiqiy sana saqlansin. */
  takenAt?: string;
}

const EXT_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  "3gp": "video/3gpp",
  mkv: "video/x-matroska",
  pdf: "application/pdf",
};

/** Ba'zi brauzerlar (masalan HEIC uchun) `file.type` ni bo'sh qoldiradi — kengaytmadan aniqlaymiz. */
export function contentTypeOf(file: { name: string; type?: string | null }): string {
  if (file.type) return file.type.split(";")[0]!.toLowerCase();
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_TYPES[ext] ?? "application/octet-stream";
}

/** Fayl turiga qarab media turini taxmin qiladi; qo'llab-quvvatlanmasa `null`. */
export function detectKind(file: { name: string; type?: string | null }): MediaKind | null {
  const ct = contentTypeOf(file);
  if (ct.startsWith("image/")) return "photo";
  if (ct.startsWith("video/")) return "video";
  if (ct === "application/pdf") return "document";
  return null;
}

export interface PartPlan {
  number: number;
  start: number;
  end: number; // end — kiritilmaydi (Blob.slice kabi)
}

export function planParts(size: number, partSize: number): PartPlan[] {
  const parts: PartPlan[] = [];
  for (let start = 0, n = 1; start < size; start += partSize, n++) {
    parts.push({ number: n, start, end: Math.min(start + partSize, size) });
  }
  return parts;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Bitta PUT (XHR — yuklash progressi uchun). 2xx bo'lmasa `status` bilan xato tashlaydi. */
export function putBlob(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  onProgress: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(Object.assign(new Error(`PUT ${xhr.status}`), { status: xhr.status }));
    xhr.onerror = () => reject(Object.assign(new Error("network"), { status: 0 }));
    xhr.onabort = () => reject(Object.assign(new Error("aborted"), { status: -1 }));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

type PutFn = typeof putBlob;

/** Yuklanadigan fayl: brauzerda `File`, mobilda `expo-file-system` `File` (ikkalasida `slice` bor). */
export interface UploadSource {
  name: string;
  type?: string;
  size: number;
  slice(start: number, end: number): Blob;
}

/** Serverdagi yuklash sessiyasi: offlayn navbat uni saqlab, keyin davom ettiradi. */
export interface UploadSession {
  id: string;
  mode: "single" | "multipart";
  partSize: number | null;
}

export interface UploadOptions {
  /** Oldingi urinishdan qolgan sessiya: multipart bo'lsa faqat yetishmayotgan bo'laklar yuklanadi. */
  session?: UploadSession;
  /** Sessiya yaratilgan zahoti chaqiriladi (qurilma o'chib qolsa ham davom ettirish uchun saqlang). */
  onSession?: (s: UploadSession) => void;
  /** Xatoda serverdagi yuklashni bekor QILMAYDI (navbat keyinroq davom ettiradi). */
  keepOnError?: boolean;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
  put?: PutFn; // testlar uchun
  retryDelayMs?: number;
}

/**
 * Faylni backend'ga o'tkazmasdan, bucket'ga to'g'ridan-to'g'ri yuklaydi.
 * Katta fayl bo'laklab yuklanadi; har bo'lak 3 marta qayta uriniladi.
 * `opts.session` berilsa va multipart bo'lsa — yuklanib bo'lgan bo'laklar qayta yuborilmaydi.
 */
export async function uploadFile(
  api: ApiClient,
  file: UploadSource,
  meta: UploadMeta,
  opts: UploadOptions = {},
): Promise<Media> {
  const put = opts.put ?? putBlob;
  const delay = opts.retryDelayMs ?? 1000;
  const contentType = contentTypeOf(file);
  const total = file.size;

  let id: string;
  let plan: { mode: "single" | "multipart"; url?: string; headers?: Record<string, string>; part_size?: number };
  let already = new Map<number, number>(); // PartNumber -> hajm (serverda bor)

  const resumed = await resumeSession(api, opts.session);
  if (resumed && "done" in resumed) return resumed.done;
  if (resumed) {
    id = resumed.id;
    plan = { mode: "multipart", part_size: resumed.partSize };
    already = resumed.parts;
  } else {
    // Eski single sessiya qayta ishlatilmaydi (imzolangan URL tugagan): tozalab, yangisini ochamiz
    if (opts.session)
      await api
        .POST("/api/media/{id}/abort/", { params: { path: { id: opts.session.id } } })
        .catch(() => undefined);
    const created = unwrap(
      await api.POST("/api/media/", {
        body: {
          property: meta.property,
          room: meta.room ?? null,
          wall: meta.wall ?? null,
          panel: meta.panel ?? null,
          kind: meta.kind,
          filename: file.name,
          content_type: contentType,
          size: total,
          note: meta.note,
          taken_at: meta.takenAt,
        },
      }),
    );
    id = created.id;
    plan = created.upload;
    opts.onSession?.({
      id,
      mode: plan.mode,
      partSize: plan.mode === "multipart" ? (plan.part_size ?? null) : null,
    });
  }
  const path = { params: { path: { id } } } as const;

  try {
    if (plan.mode === "single") {
      await put(
        plan.url!,
        file.slice(0, total),
        plan.headers ?? { "Content-Type": contentType },
        (l) => opts.onProgress?.(l, total),
        opts.signal,
      );
    } else {
      const parts = planParts(total, plan.part_size!);
      let done = 0;
      const urls = new Map<number, string>();
      const fetchUrls = async (numbers: number[]) => {
        const res = unwrap(
          await api.POST("/api/media/{id}/upload-parts/", {
            ...path,
            body: { part_numbers: numbers },
          }),
        );
        for (const [n, u] of Object.entries(res.urls)) urls.set(Number(n), u as string);
      };
      const missing = parts.filter((p) => already.get(p.number) !== p.end - p.start);
      for (const p of parts) if (!missing.includes(p)) done += p.end - p.start;
      opts.onProgress?.(done, total);
      for (let i = 0; i < missing.length; i += 25)
        await fetchUrls(missing.slice(i, i + 25).map((p) => p.number));

      for (const part of missing) {
        const blob = file.slice(part.start, part.end);
        for (let attempt = 1; ; attempt++) {
          try {
            await put(
              urls.get(part.number)!,
              blob,
              {},
              (l) => opts.onProgress?.(done + l, total),
              opts.signal,
            );
            break;
          } catch (e) {
            const status = (e as { status?: number }).status;
            if (status === -1 || attempt >= 3) throw e;
            await sleep(delay * 2 ** (attempt - 1));
            if (status === 403) await fetchUrls([part.number]); // imzolangan URL muddati tugagan bo'lishi mumkin
          }
        }
        done += part.end - part.start;
        opts.onProgress?.(done, total);
      }
    }
  } catch (e) {
    if (!opts.keepOnError) await api.POST("/api/media/{id}/abort/", path).catch(() => undefined);
    throw e;
  }

  let body: { parts?: { PartNumber: number; ETag: string }[] } = {};
  if (plan.mode === "multipart") {
    // ETag'larni serverdan olamiz: brauzerda ETag sarlavhasi CORS bilan yopiq bo'lishi mumkin
    const st = unwrap(await api.GET("/api/media/{id}/upload-status/", path));
    body = { parts: st.parts.map((p) => ({ PartNumber: p.PartNumber, ETag: p.ETag })) };
  }
  opts.onProgress?.(total, total);
  return unwrap(await api.POST("/api/media/{id}/complete/", { ...path, body: body as never }));
}

type Resumed =
  | { id: string; partSize: number; parts: Map<number, number> }
  | { done: Media }
  | null;

/**
 * Multipart sessiyani tekshiradi. `null` — davom ettirib bo'lmaydi (yangisini boshlash kerak).
 * Server allaqachon yakunlagan bo'lsa (javob yo'qolgan edi) — tayyor yozuv qaytadi: dublikat bo'lmaydi.
 */
async function resumeSession(api: ApiClient, session: UploadSession | undefined): Promise<Resumed> {
  if (!session || session.mode !== "multipart" || !session.partSize) return null;
  const path = { params: { path: { id: session.id } } } as const;
  const st = await api.GET("/api/media/{id}/upload-status/", path);
  if (st.response.status === 404 || st.response.status === 400) return null; // sessiya o'chirilgan
  const data = unwrap(st);
  if (data.status !== "pending") return { done: unwrap(await api.GET("/api/media/{id}/", path)) };
  return {
    id: session.id,
    partSize: session.partSize,
    parts: new Map(data.parts.map((p) => [p.PartNumber, p.Size])),
  };
}
